import React from 'react'
import { inject, observer } from 'mobx-react'
import { Alert, message, Progress, Spin } from 'antd-v4'
import { Icon, Upload } from 'antd'
import { UploadFile } from 'antd/lib/upload/interface'
import { UploadChangeParam } from 'antd-v4/lib/upload'
import {
  parseErrorMsg,
  parseProductBulkUpdateErrorMsg,
  parseProductBulkUpdateErrorTitle,
  sleep,
  uploadFileToS3Bucket,
} from '../../../../utilities/helpers'
import { ProductModels, productApi } from '@getgreenline/products'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { isArray } from 'lodash'
import { CloseCircleFilled } from '@ant-design/icons'
import { PermissionNames } from '@getgreenline/homi-shared'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'

interface IFileObject {
  originFileObj: File | Blob
  fullFileName: string
  fileExtension: string
  contentType: string
}

const permittedFileTypes = ['csv', 'xls', 'xlsx']

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  showSuccessMessage: boolean
  setShowSuccessMessage: (show: boolean) => void
  errorMessages: string[] | ProductModels.IBulkError[]
  setErrorMessages: (errors: string[]) => void
  errorTitle: string
  setErrorTitle: (title: string) => void
  afterImport: () => void
}

interface State {
  isUploading: boolean
  fileObject?: IFileObject
  uploadStepsTaken: number
  progressMessage: string
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class ImportProductsPricesDropZone extends React.Component<Props, State> {
  readonly company = this.props.currentCompanyStore!.company!
  totalProgressSteps = 4

  constructor(props: Props) {
    super(props)

    this.state = {
      isUploading: false,
      uploadStepsTaken: 0,
      progressMessage: '',
    }
  }

  get progressRate() {
    const { uploadStepsTaken } = this.state
    return Math.floor((uploadStepsTaken / this.totalProgressSteps) * 100)
  }

  get acceptFileTypes() {
    return `.${permittedFileTypes.join(',.')}`
  }

  finishUploading = () =>
    this.setState({
      isUploading: false,
      uploadStepsTaken: 0,
    })

  onDrop = async (file: UploadFile<any>) => {
    const { setShowSuccessMessage, setErrorMessages } = this.props
    try {
      setShowSuccessMessage(false)
      setErrorMessages([])

      this.setState({
        isUploading: true,
        progressMessage: 'Uploading file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      await this.validateDroppedFile(file)
      await this.uploadFile()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while handling the file', 10)
      this.finishUploading()
    }
  }

  validateDroppedFile = async (file: UploadFile<any>) => {
    const { name, type, originFileObj } = file
    const fileExtension = name.split('.').pop()

    if (!fileExtension || !this.hasCorrectFileExtension(fileExtension)) {
      this.showFileTypeErrorMessage()
      this.finishUploading()
      return
    }

    if (!originFileObj) {
      this.finishUploading()
      throw new Error(`The originFileObj of ${name} is unavailable.`)
    }

    this.setState({
      fileObject: {
        originFileObj,
        fullFileName: name,
        fileExtension,
        contentType: type,
      },
    })
  }

  hasCorrectFileExtension = (fileExtension: string) => {
    return permittedFileTypes.includes(fileExtension)
  }

  showFileTypeErrorMessage = () => {
    message.error(
      <>
        <span>Please upload a file with one of the following extensions:</span>
        <ul className='no-margin-list' style={{ width: 'fit-content' }}>
          {permittedFileTypes.map((ext, index) => (
            <li key={index}>{ext}</li>
          ))}
        </ul>
      </>,
      10,
    )
  }

  uploadFile = async () => {
    if (!this.state.fileObject) {
      this.finishUploading()
      return
    }

    const { originFileObj, fileExtension, contentType } = this.state.fileObject

    try {
      const { fileName, signedUrl } =
        await this.props.currentCompanyStore!.getSignedTempFileUploadUrl(fileExtension, contentType)

      this.setState({
        progressMessage: 'Uploading file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      this.handleS3Upload(originFileObj, fileName, signedUrl, contentType)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while uploading the file', 10)
      this.finishUploading()
    }
  }

  handleS3Upload = (
    file: File | Blob,
    fileName: string,
    signedUrl: string,
    contentType: string,
  ): void => {
    const xhr = uploadFileToS3Bucket(file, signedUrl, contentType)

    xhr.onload = async () => {
      if (xhr.status !== 200) {
        this.finishUploading()
        return message.error('An error occurred while uploading your file')
      }

      await this.xhrOnload(fileName)
    }
  }

  xhrOnload = async (fileName: string) => {
    try {
      this.setState({
        progressMessage: 'Uploading file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      await sleep(500) // to show the progress bar properly

      const receiptResponse = await productApi.importProductPrices(this.company.id, {
        fileName,
      })

      message.success(`Successfully imported`, 5)

      this.setState({
        progressMessage: 'Uploading file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      this.props.setShowSuccessMessage(true)

      await sleep(2000) // show the completed bar for 2 seconds
      this.finishUploading()
      this.props.afterImport()
    } catch (error) {
      const errorMessage = parseProductBulkUpdateErrorMsg(error)
      const errorTitle = parseProductBulkUpdateErrorTitle(error)

      if (errorMessage === undefined && isArray(errorTitle)) {
        const [mainTitle, ...remainingErrors] = errorTitle
        this.props.setErrorTitle(mainTitle)
        this.props.setErrorMessages(remainingErrors)
      } else if (isArray(errorMessage)) {
        this.props.setErrorTitle(errorTitle || 'Error validating file')
        this.props.setErrorMessages(errorMessage)
      } else {
        message.error(errorMessage || 'An error occurred while processing your file', 10)
      }

      this.finishUploading()
    }
  }

  showErrorMessages = () => {
    const { errorMessages, errorTitle } = this.props

    return (
      <Alert
        className='mb-3 mt-4'
        type='error'
        message={
          <>
            <div className='font-weight-bold mb-1'>{errorTitle}</div>
            <ul className='pl-0 mb-0'>{this.renderErrorMessages(errorMessages)}</ul>
          </>
        }
      />
    )
  }

  renderErrorMessages = (errorMessages: string[] | ProductModels.IBulkError[]) => {
    return errorMessages.map((error, index) => (
      <li
        key={index}
        style={{
          listStyle: 'none',
          display: 'flex',
          alignItems: 'center',
          marginBottom: '0.6rem',
        }}
      >
        <div style={{ marginRight: '0.6rem' }}>
          <CloseCircleFilled style={{ color: 'red' }} />
        </div>
        {typeof error === 'object' && this.showObjectErrors(error)}
        {typeof error === 'string' && error}
      </li>
    ))
  }

  showObjectErrors = (error: ProductModels.IBulkError) => {
    return (
      <>
        {error.id && `Error processing row with product ID ${error.id}.`} {error.message}
      </>
    )
  }

  render() {
    const { isUploading, progressMessage } = this.state
    const { errorMessages, showSuccessMessage } = this.props

    const canEditProducts = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_PRODUCTS,
      this.company.id,
    )

    const baseDraggerStyle = {
      margin: '18px 0px',
    }

    if (isUploading) {
      return (
        <div style={{ marginTop: '50px' }}>
          <Progress percent={this.progressRate} />
          <div className='d-flex align-items-center justify-content-center mt-2'>
            <span className='mr-2'>{progressMessage}</span>
            <Spin className='mt-1' size='small' />
          </div>
        </div>
      )
    }

    return (
      <div className='mt-4'>
        {!canEditProducts && (
          <Alert
            className='mb-3'
            type='warning'
            showIcon
            style={{ width: 340 }}
            message={<span>You do not have permission to edit products.</span>}
          />
        )}

        <Upload.Dragger
          name='file'
          multiple={false}
          onChange={(info: UploadChangeParam<UploadFile<any>>) => this.onDrop(info.file)}
          style={
            canEditProducts
              ? baseDraggerStyle
              : { ...baseDraggerStyle, backgroundColor: 'rgba(0, 0, 0, 0.1)', opacity: 0.7 }
          }
          accept={this.acceptFileTypes}
          showUploadList={false}
          className='mt-2'
          disabled={!canEditProducts}
        >
          <p className='ant-upload-drag-icon'>
            <Icon type='inbox' />
          </p>
          <div className='ant-upload-text'>Click or drag file to upload</div>
        </Upload.Dragger>

        {errorMessages.length > 0 && this.showErrorMessages()}

        {showSuccessMessage && (
          <Alert
            message={<div className='text-center'>File successfully imported.</div>}
            showIcon
            type='success'
            style={{ width: 230 }}
          />
        )}
      </div>
    )
  }
}
